import React, { useCallback, useEffect, useRef, useState } from 'react';
import SchoolHeader from '@/components/SchoolHeader';
import SummaryBox from '@/components/SummaryBox';
import CtaButton from '@/components/CtaButton';
import ErrorBanner from '@/components/ErrorBanner';
import PaymentMethodSection from '@/payment/PaymentMethodSection';
import { setDefaultPaymentMethod, toggleAutoPay } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import { getStripe } from '@/services/stripe';
import { amountBreakdownRows, formatDate } from '@/format';
import session from '@/session';

/**
 * Web·2 — enrollment. Card entry stays inside Stripe (PaymentElement);
 * the mandate checkbox and the enable action are ours. Also owns the
 * post-3DS-redirect resumption (resumeClientSecret).
 */
const SetUpScreen = ({
	school,
	status,
	assignment,
	studentName,
	onEnabled,
	onPayInvoiceInstead,
	resumeClientSecret,
	onResumeConsumed,
	assignmentSwitcher,
}) => {
	const [mandate, setMandate] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [forceExpanded, setForceExpanded] = useState(false);
	const [selection, setSelection] = useState(status.default_payment_method ? { type: 'default' } : { type: 'new' });
	const confirmRef = useRef(null);
	const handleBridge = useCallback((fn) => {
		confirmRef.current = fn;
	}, []);

	const studentId = status.student_id;
	const schoolName = school?.name ?? 'your school';
	const firstChargeAt = assignment.next_charge_at ?? assignment.first_charge_at;

	const finishEnable = useCallback(
		async (assignmentId) => {
			await toggleAutoPay(assignmentId, { enabled: true, mandate_accepted: true });
			session.clearPendingSetup();
			onEnabled();
		},
		[onEnabled]
	);

	// Post-3DS return: the bank redirect landed back here with the intent's
	// client secret in the query. Pick the sequence back up where it left off.
	useEffect(() => {
		if (!resumeClientSecret || !status.stripe_account_id) return;

		const resume = async () => {
			setBusy(true);
			try {
				const stripe = await getStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, status.stripe_account_id);
				const { setupIntent } = await stripe.retrieveSetupIntent(resumeClientSecret);
				const pending = session.getPendingSetup();

				if (setupIntent?.status === 'succeeded' && pending?.mandate) {
					await setDefaultPaymentMethod(setupIntent.payment_method, pending.student_id ?? studentId);
					await finishEnable(pending.assignment_id ?? assignment.assignment_id);
					return;
				}
				setError('Your card wasn’t fully set up — please try again.');
			} catch (err) {
				setError(parseHostedError(err).message);
			} finally {
				setBusy(false);
				onResumeConsumed();
			}
		};

		resume();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resumeClientSecret, status.stripe_account_id]);

	const handleEnable = async () => {
		setError(null);
		setBusy(true);
		try {
			if (selection.type === 'new') {
				if (!confirmRef.current) {
					setForceExpanded(true);
					setError('Please add a payment method first.');
					return;
				}
				session.savePendingSetup({
					assignment_id: assignment.assignment_id,
					student_id: studentId,
					mandate: true,
				});
				const returnUrl = `${window.location.origin}/auto-pay${window.location.search}`;
				const { setupIntent, error: stripeError } = await confirmRef.current(returnUrl);
				if (stripeError) {
					setError(stripeError.message);
					return;
				}
				await setDefaultPaymentMethod(setupIntent.payment_method, studentId);
			} else if (selection.type === 'saved' && !selection.isDefault) {
				await setDefaultPaymentMethod(selection.pmId, studentId);
			}

			await finishEnable(assignment.assignment_id);
		} catch (err) {
			const parsed = parseHostedError(err);
			if (parsed.code === 'payment_method_required') setForceExpanded(true);
			setError(parsed.message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="flex flex-col gap-[18px]">
			<SchoolHeader name={school?.name} logo={school?.logo} />

			<h1 className="text-[24px] font-extrabold text-[#16181d]">Set up Auto-Pay</h1>

			<p className="text-[14px] leading-[21px] text-[#7d7d7d]">
				Auto-Pay keeps {studentName ? `${studentName}’s` : 'your student’s'} tuition on time — we’ll charge your
				selected method automatically when each invoice is due.
			</p>

			{assignmentSwitcher}

			<SummaryBox
				rows={[
					{ label: 'Student', value: studentName ?? '—' },
					...amountBreakdownRows(assignment),
					{ label: 'First charge', value: firstChargeAt ? formatDate(firstChargeAt) : '—' },
				]}
			/>

			<PaymentMethodSection
				studentId={studentId}
				stripeAccountId={status.stripe_account_id}
				defaultPm={status.default_payment_method}
				onSelectionChange={setSelection}
				onBridge={handleBridge}
				forceExpanded={forceExpanded}
			/>

			<label className="flex w-full cursor-pointer items-start gap-[10px] pt-[2px]">
				<input
					type="checkbox"
					checked={mandate}
					onChange={(event) => setMandate(event.target.checked)}
					className="mt-[2px] h-[20px] w-[20px] shrink-0 accent-[#028afd]"
				/>
				<span className="text-[12.5px] leading-[18px] text-[#7d7d7d]">
					I authorize {schoolName} to automatically charge this payment method for each tuition invoice until I turn off
					Auto-Pay.
				</span>
			</label>

			<ErrorBanner>{error}</ErrorBanner>

			<CtaButton onClick={handleEnable} disabled={busy || !mandate}>
				{busy ? 'Turning on…' : 'Turn on Auto-Pay'}
			</CtaButton>

			{(status.open_invoice_count ?? 0) > 0 ? (
				<button
					type="button"
					onClick={onPayInvoiceInstead}
					className="w-full text-center text-[13px] font-semibold text-[#028afd]"
				>
					Pay a single invoice instead →
				</button>
			) : null}
		</div>
	);
};

export default SetUpScreen;
